import "./Homepage.css";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

function HomePage({ setPage }) {
  const [login, setLogin] = useState(false);
  const [user, setUser] = useState('');
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState();
  const [sessionId, setSessionId] = useState(null);
  const [expired, setExpired] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Plan options
  const planOptions = [
    { value: 'visitor', label: 'Visitor Level (Free)' },
    { value: 'operator', label: 'Operator Training Package' },
    { value: 'engineer', label: 'Process Engineer Suite' },
    { value: 'classroom', label: 'Classroom' },
    { value: 'admin', label: 'Administrator' }
  ];

  // Plan to page mapping
  const planPageMapping = {
    'visitor': 'CrudeTank1',
    'operator': 'OperatorDashboard',
    'engineer': 'EngineerDashboard',
    'classroom': 'ClassroomDashboard'
  };

  useEffect(() => {
    const fetchUserData = async () => {
      // 1. Check localStorage first
      const storedUserData = localStorage?.getItem('dcs_simulator_user_data');
      if (storedUserData) {
        window.history.replaceState({}, document.title, window.location.pathname);

        try {
          const parsedData = JSON.parse(storedUserData);
          console.log("expiredDate", parsedData);

          if (new Date() > parsedData.expiresAt) {
            Swal.fire({
              icon: 'error',
              title: 'Your session has expired. Please login again.',
              showCancelButton: true,
              confirmButtonText: 'OK',
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                localStorage.removeItem('dcs_simulator_user_data');
                window.location.href = "https://nktechhub.com/signup";
              }
            });
            return;
          }
          setUserId(parsedData.id);
          setUserData(parsedData.userData);
          console.log('✅ User data loaded from localStorage', parsedData);
          return;
        } catch (error) {
          console.error('Error parsing localStorage data:', error);
          return;
        }
      }

      // 2. If no localStorage, check session ID in URL
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session');

      if (!sessionId) {
        console.log('❌ No session ID found and no localStorage data found');
      }

      console.log("sessionId", sessionId);

      try {
        const response = await fetch(`https://api.nktechhub.com/api/dcs/simulator/get-session/${sessionId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to get session data');
        }

        const data = await response.json();
        console.log("data", data);
        setUserId(data.userId);
        setUserData(data);
        localStorage.setItem('dcs_simulator_user_data', JSON.stringify(data));
        console.log('✅ User data retrieved from session');
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('❌ Error fetching session:', error);
      }
    };

    fetchUserData();
  }, []);

  const handleSignIn = async () => {
    setLoading(true);
    if (userData) {
      // Check if a plan is selected
      if (!selectedPlan) {
        Swal.fire({
          icon: 'warning',
          title: 'Select a Plan',
          text: 'Please select your purchased plan before continuing.',
          confirmButtonText: 'OK'
        }).then((result) => {
          setLoading(false);
        })
        
        return;
      }

      if (selectedPlan === 'admin') {
        Swal.fire({
          title: "Access Restricted",
          text: "Admin access is restricted to authorized personnel only.",
          icon: "info",
          input: "password",
          inputPlaceholder: "Enter password",
          inputAttributes: {
            autocapitalize: "off",
            autocorrect: "off"
          },
          showCancelButton: true,
          confirmButtonText: "Access Simulator",
          cancelButtonText: "Cancel",
          preConfirm: (password) => {
            if (!password) {
              Swal.showValidationMessage("Password is required");
              return false;
            }
            return password;
          }
        }).then(async (result) => {  // ✅ Added 'async' here

          if (result.dismiss === Swal.DismissReason.cancel || result.dismiss === Swal.DismissReason.backdrop) {
            setLoading(false);  // ✅ Turn off loading when cancelled
            return;
          }

          if (result.isConfirmed) {
            const enteredPassword = result.value;
            console.log(userData);
            const userEmail = userData?.userData?.email || userData?.email;

            try {
              const response = await fetch(`https://api.nktechhub.com/api/dcs/adminpassword?password=${enteredPassword}&email=${userEmail}`, {
                credentials: 'include'
              });

              if (!response.ok) {
                throw new Error('Failed to get session data');
              }

              const data = await response.json();
              console.log("correctPassword", data);

              // Check if the response indicates correct password
              if (data.message === "Password verified") {
                setLoading(false);
                Swal.fire({
                  icon: "success",
                  title: "Access Granted",
                  text: "Redirecting...",
                  timer: 1500,
                  showConfirmButton: false
                }).then(() => {
                  setPage("CrudeTank1");
                });
              } else {
                setLoading(false);
                Swal.fire({
                  icon: "error",
                  title: "Wrong Password",
                  text: data.message || "Incorrect password. Please contact your instructor for access.",
                  confirmButtonText: "Try Again"
                });
              }
            } catch (error) {
              console.error('❌ Error fetching password:', error);
              setLoading(false);
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to verify password. Please try again later."
              });
            }
          }
        });
      } else if (selectedPlan === 'classroom') {
        Swal.fire({
          title: 'Classroom Plan',
          html: `
      <div style="text-align: left; color: #fff;">
        <div style="text-align: center; margin-bottom: 20px;">
          <span style="display: inline-block; background: linear-gradient(135deg, #ffaa00, #ff6b6b); color: #fff; padding: 4px 16px; border-radius: 20px; font-size: 12px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase;">
            🚀 Coming Soon
          </span>
        </div>

        <h3 style="color: #00ff88; margin-bottom: 15px;">What is the Classroom Plan?</h3>
        <p style="margin-bottom: 15px; line-height: 1.6;">
          The Classroom Plan is designed specifically for <strong style="color: #00ff88;">educational institutions</strong> 
          and <strong style="color: #00ff88;">training centers</strong>. It allows instructors to teach refinery operations 
          to students in a controlled, interactive environment.
        </p>
        
        <div style="background: rgba(0, 255, 136, 0.05); padding: 15px; border-radius: 10px; margin: 15px 0; border: 1px solid rgba(0, 255, 136, 0.1);">
          <h4 style="color: #00ff88; margin-bottom: 10px;">Student Capacity</h4>
          <p style="margin-bottom: 5px;">Supporting <strong style="color: #fff;">10 to 500 students</strong> simultaneously.</p>
          <ul style="margin: 10px 0 0 20px; line-height: 1.8;">
            <li>🔹 <strong>Small classes (10-50):</strong> Perfect for specialized training groups</li>
            <li>🔹 <strong>Medium classes (50-200):</strong> Ideal for university departments</li>
            <li>🔹 <strong>Large classes (200-500):</strong> Suitable for entire institutions</li>
          </ul>
        </div>

        <div style="background: rgba(255, 170, 0, 0.05); padding: 15px; border-radius: 10px; margin: 15px 0; border: 1px solid rgba(255, 170, 0, 0.1);">
          <h4 style="color: #ffaa00; margin-bottom: 10px;">Key Features</h4>
          <ul style="margin-left: 20px; line-height: 1.8;">
            <li>✅ <strong>Instructor Dashboard</strong> - Monitor all student progress</li>
            <li>✅ <strong>Student Management</strong> - Add/remove students easily</li>
            <li>✅ <strong>Real-time Tracking</strong> - See who's online and what they're doing</li>
            <li>✅ <strong>Classroom Exercises</strong> - Pre-built training scenarios</li>
            <li>✅ <strong>Progress Reports</strong> - Track student performance over time</li>
            <li>✅ <strong>Multi-User Access</strong> - Full refinery simulation for all students</li>
          </ul>
        </div>

        <div style="background: rgba(100, 100, 255, 0.05); padding: 15px; border-radius: 10px; margin: 15px 0; border: 1px solid rgba(100, 100, 255, 0.1);">
          <h4 style="color: #6666ff; margin-bottom: 10px;">📊 Use Cases</h4>
          <ul style="margin-left: 20px; line-height: 1.8;">
            <li><strong>Universities:</strong> Chemical engineering, petroleum engineering courses</li>
            <li><strong>Technical Schools:</strong> Vocational training for refinery operations</li>
            <li><strong>Corporate Training:</strong> New employee onboarding and upskilling</li>
            <li><strong>Research Institutions:</strong> Process optimization research</li>
          </ul>
        </div>

        <div style="background: rgba(255, 170, 0, 0.08); padding: 15px; border-radius: 10px; margin: 15px 0; border: 1px solid rgba(255, 170, 0, 0.2);">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
            <span style="font-size: 24px;">🔔</span>
            <h4 style="color: #ffaa00; margin: 0;">We're Building Something Amazing!</h4>
          </div>
          <p style="color: #b8c7d6; margin: 0; line-height: 1.6;">
            The Classroom Plan is currently under development. We're working hard to bring you the best 
            educational experience for refinery operations training. 
            <strong style="color: #ffaa00;">Stay tuned for updates!</strong>
          </p>
        </div>

        <div style="background: rgba(255, 59, 59, 0.05); padding: 15px; border-radius: 10px; margin: 15px 0; border: 1px solid rgba(255, 59, 59, 0.1);">
          <h4 style="color: #ff3b3b; margin-bottom: 10px;">⚠️ Important Notes</h4>
          <ul style="margin-left: 20px; line-height: 1.8; color: #ff6b6b;">
            <li>🔴 This plan is <strong>strictly for educational/training purposes</strong></li>
            <li>🔴 Not intended for commercial refinery operations</li>
            <li>🔴 Requires <strong>instructor approval</strong> for activation</li>
            <li>🔴 Each student needs their own account</li>
          </ul>
        </div>
      </div>
    `,
          icon: 'info',
          confirmButtonText: '✅ Got it!',
          cancelButtonText: 'Close',
          showCancelButton: true,
          showCloseButton: true,
          width: 700,
          padding: '20px',
          background: '#1a2332',
          color: '#ffffff',
          confirmButtonColor: '#00ff88',
          cancelButtonColor: '#6c757d',
          backdrop: 'rgba(0,0,0,0.8)',
          customClass: {
            container: 'classroom-swal-container',
            popup: 'classroom-swal-popup',
            confirmButton: 'classroom-confirm-btn',
            cancelButton: 'classroom-cancel-btn'
          }
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel ||
            result.dismiss === Swal.DismissReason.backdrop ||
            result.dismiss === Swal.DismissReason.close) {
            setLoading(false);  // ✅ Turn off loading when cancelled
            return;
          }

          if (result.isConfirmed) {

            // Close the modal - do nothing else
            setLoading(false);
            console.log('Classroom Plan modal closed');
            return;
          }
        });
      }


      else {
        // Navigate to the page based on selected plan
        Swal.fire({
          icon: 'warning',
          title: 'Still working on this feature',
          text: 'Please try again later.',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then(() => {
          setLoading(false);
        });
        //setPage(targetPage);
      }
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'You are not logged in!',
        text: 'Please sign in to continue.',
        showCancelButton: true,
        confirmButtonText: 'Sign In',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "https://nktechhub.com/signup";
        }
      });
    }
  };

  // Handle plan selection change
  const handlePlanChange = (e) => {
    setSelectedPlan(e.target.value);
    console.log('Selected plan:', e.target.value);
  };

  return (
    <div className="page-container">
      {/* User Profile */}
      {userData && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          padding: '10px 20px',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 9999,
          transition: 'all 0.3s ease',
          cursor: 'default',
          fontFamily: 'Inter, -apple-system, sans-serif'
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
          }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00ff88, #00cc66)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#001018',
            flexShrink: 0
          }}>
            {userData.firstName?.[0] || userData.userData.firstName?.[0] || 'U'}
          </div>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#ffffff',
              lineHeight: 1.2
            }}>
              {userData.firstName || userData.userData.firstName || 'User'}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.2
            }}>
              Welcome
            </div>
          </div>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#00ff88',
            boxShadow: '0 0 12px rgba(0, 255, 136, 0.6)',
            flexShrink: 0
          }} />
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="bg-effects">
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
        <div className="glow-orb orb-3"></div>
      </div>

      {/* HEADER */}
      <div className="page-header">
        <div className="header-badge">
          <span className="status-dot"></span>
          SYSTEM ONLINE • v3.2
        </div>
        <h1>
          <span className="highlight">NK TECH</span>
          <br />
          REFINERY DCS SYSTEM
        </h1>
        <div className="header-sub">
          <span className="divider-line"></span>
          <h2>30,000 BPD MODULAR REFINERY</h2>
          <span className="divider-line"></span>
        </div>
        <p className="header-description">
          Distributed Control System for Real-Time Refinery Operations
        </p>
      </div>

      {/* BODY - Cards Grid */}
      <div className="page-body">
        {/* Card 1: Process Overview */}
        <div className="dcs-panel panel-1">
          <div className="panel-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div className="panel-content">
            <h3>PROCESS OVERVIEW</h3>
            <p>
              This Distributed Control System (DCS) simulates refinery-wide operations
              including crude receiving, tank farm operations, preheat train systems,
              desalting, fractionation, stabilization, utility systems, and refinery
              process monitoring.
            </p>
            <div className="panel-tag">Real-Time Monitoring</div>
          </div>
        </div>

        {/* Card 2: Industrial Control */}
        <div className="dcs-panel panel-2">
          <div className="panel-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <div className="panel-content">
            <h3>INDUSTRIAL CONTROL</h3>
            <p>
              Operators can monitor process conditions, alarms, transmitters,
              control valves, pumps, furnaces, towers, and refinery process units
              in real time with full operational visibility.
            </p>
            <div className="panel-tag">SCADA Integration</div>
          </div>
        </div>

        {/* Card 3: Simulator Access */}
        <div className="dcs-panel panel-3">
          <div className="panel-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className="panel-content">
            <h3>SIMULATOR ACCESS</h3>
            <p>
              Click the sign in button below to access the refinery DCS
              operational environment and begin monitoring your processes.
            </p>

            {/* Plan Selection Dropdown */}
            <div style={{
              margin: '15px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}>
              <label style={{
                color: '#b8c7d6',
                fontSize: '14px',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Select Your Plan
              </label>
              <select
                value={selectedPlan}
                onChange={handlePlanChange}
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '36px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#00ff88';
                  e.target.style.boxShadow = '0 0 20px rgba(0, 255, 136, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.15)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="" style={{ background: '#1a2332', color: '#8899aa' }}>
                  -- Select your plan --
                </option>
                <option value="visitor" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Visitor Level (Free)
                </option>
                <option value="operator" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Operator Training Package
                </option>
                <option value="engineer" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Process Engineer Suite
                </option>
                <option value="classroom" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Classroom
                </option>
                <option value="admin" style={{ background: '#1a0032', color: '#ffffff' }}>
                  Administrator
                </option>
              </select>
            </div>

            {/* Show selected plan info */}
            {selectedPlan && (
              <div style={{
                marginBottom: '12px',
                padding: '8px 16px',
                borderRadius: '8px',
                background: 'rgba(0, 255, 136, 0.08)',
                border: '1px solid rgba(0, 255, 136, 0.15)',
                color: '#00ff88',
                fontSize: '13px',
                display: 'inline-block'
              }}>
                ✅ Selected: {planOptions.find(p => p.value === selectedPlan)?.label}
              </div>
            )}

            <button className="signin-btn" onClick={handleSignIn}>
              <span className="btn-icon">
                {loading ? (
                  // ✅ Spinner SVG
                  <svg className="spinner" viewBox="0 0 50 50" style={{
                    width: '20px',
                    height: '20px',
                    animation: 'spin 0.8s linear infinite'
                  }}>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4"
                      strokeDasharray="31.4 31.4" strokeLinecap="round" />
                  </svg>
                ) : (
                  '▶'
                )}
              </span>
              {loading ? 'PROCESSING...' : 'ACCESS SIMULATOR'}
              <span className="btn-shimmer"></span>
            </button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="page-footer">
        <div className="footer-stats">
          <div className="stat-item">
            <span className="stat-value">24/7</span>
            <span className="stat-label">Operations</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">99.9%</span>
            <span className="stat-label">Uptime</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">Secure</span>
            <span className="stat-label">Connection</span>
          </div>
        </div>
        <p className="footer-copyright">
          © {new Date().getFullYear()} NK Tech Refinery DCS System. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default HomePage;